import type { ButtonHTMLAttributes, ReactNode } from 'react'

/*
 * `amber` is gone. It was kept as an alias through the redesign so the colour
 * change and the rename did not land in one unreviewable commit, and every one
 * of its call sites has since been audited: all thirty-three were primary brand
 * actions, none meant semantic warning. Keeping the name would have left brand
 * saffron and warning amber looking like the same idea, which they are not.
 */
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'md' | 'sm'

const FILLED =
  'bg-brand-600 text-white shadow-sm hover:bg-brand-700 disabled:hover:bg-brand-600'
const OUTLINE =
  'bg-surface border border-line text-ink-secondary shadow-sm hover:bg-surface-subtle hover:text-ink disabled:hover:bg-surface'

const VARIANTS: Record<Variant, string> = {
  primary: FILLED,
  secondary: OUTLINE,
  // No border and no surface until hovered: for the third action in a row,
  // where a third box would be noise.
  ghost: OUTLINE,
  danger:
    'bg-surface border border-bad/30 text-bad shadow-sm hover:bg-bad-soft disabled:hover:bg-surface',
}

/*
 * Taller on a phone, unchanged on a desktop.
 *
 * A finger needs about 44px and a mouse does not, so the height is the one
 * thing about a button that should differ between them. Every page's primary
 * action used to be 36px on a phone, which is the control people press most and
 * the one hardest to hit, and the measurement pass for the presentation
 * milestone found it on every list screen at once.
 *
 * Set here rather than at each call site so it cannot drift: a caller that
 * needs something different still passes `className`, which is applied after
 * this and wins.
 */
const SIZES: Record<Size, string> = {
  md: 'h-11 sm:h-9 px-3.5 text-[0.875rem]',
  sm: 'h-10 sm:h-8 px-3 text-[0.8125rem]',
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  children: ReactNode
}

/**
 * One filled variant, so a page never has two competing primary actions.
 * Sentence case at a readable weight: the uppercase treatment applied
 * to every button was a large part of why the product read as a machine
 * console.
 */
export default function Button({
  variant = 'ghost',
  size = 'md',
  className = '',
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg font-semibold transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-45 ${VARIANTS[variant]} ${SIZES[size]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  )
}
